import React, { useState, useRef, useCallback } from "react";
import { useSimulation } from "@/context/SimulationContext";
import { useI18n } from "@/context/LanguageContext";
import PhaseHeader from "@/components/PhaseHeader";
import PhaseFooter from "@/components/PhaseFooter";
import AnimatedNumber from "@/components/AnimatedNumber";
import { Button } from "@/components/ui/button";
import { Download, Save, Award, Clock, Building2, AlertCircle, CalendarClock, Globe, Loader2 } from "lucide-react";
import { createSimulation, updateSimulation, createPdfExport, getSettings, apiClient } from "@/lib/api";
import { toast } from "sonner";
import { computeSavings } from "@/lib/simulation-defaults";
import { LANGUAGES } from "@/i18n/translations";
import PdfTechnicalReport from "@/components/pdf/PdfTechnicalReport";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { validatePdfTranslations, SUPPORTED_PDF_LANGS } from "@/i18n/pdfI18n";
import PdfVisualReport from "@/components/pdf/PdfVisualReport";

// -----------------------------------------------------------------
// Fetch the brand logo once and cache it as a base64 data URL so the
// backend headless Chromium never has to fetch it over the network
// (which was failing intermittently and hiding the logo from the PDF).
// -----------------------------------------------------------------
let _logoDataUrlPromise = null;
function loadLogoAsDataUrl() {
  if (_logoDataUrlPromise) return _logoDataUrlPromise;
  _logoDataUrlPromise = fetch("/brand/primo-logo.png")
    .then((r) => (r.ok ? r.blob() : Promise.reject(new Error("logo fetch failed"))))
    .then(
      (blob) =>
        new Promise((resolve, reject) => {
          const reader = new FileReader();
          reader.onload = () => resolve(String(reader.result));
          reader.onerror = () => reject(reader.error);
          reader.readAsDataURL(blob);
        })
    )
    .catch(() => null);
  return _logoDataUrlPromise;
}

export default function Phase5Reveal() {
  const { state, setState, goTo } = useSimulation();
  const { t, lang: appLang } = useI18n();
  const [saving, setSaving] = useState(false);
  const [showLangDialog, setShowLangDialog] = useState(false);
  const [pdfLang, setPdfLang] = useState(null);
  const [pdfVariant, setPdfVariant] = useState("visual"); // "visual" | "technical"
  const [logoDataUrl, setLogoDataUrl] = useState(null);
  const [exporting, setExporting] = useState(false);
  const pdfRef = useRef(null);

  // Preload the logo once when the phase mounts.
  React.useEffect(() => {
    loadLogoAsDataUrl().then((url) => {
      if (url) setLogoDataUrl(url);
    });
  }, []);

  const s = computeSavings(state);


  const priceCurrent = s.priceCustomer;
  const pricePrimo = s.pricePrimo;
  const priceSavings = s.priceSavings;

  // Time is in minutes internally; display in hours.
  const timeSavingsMin = s.timeSavingsMin;
  const timeSavingsH = s.timeSavingsH;

  const fleet = s.fleet;
  const doors = s.doors;
  const quantity = s.quantity;
  const period = s.period;
  const periodLabel = t(
    period === "weekly" ? "trailer.weekly" : period === "monthly" ? "trailer.monthly" : "trailer.annualPeriod"
  );
  const annualMultiplier = s.annualMult;

  // Savings for the SELECTED period (per fleet). One "set" = one full trailer rear closure
  // (already includes both frames). Do NOT multiply by doors here.
  const periodSavings = s.periodSavings;
  const annualSavings = s.annualSavings;
  const periodTimeH = s.periodTimeH; // hours saved per period
  const annualTimeH = s.annualTimeH; // hours saved per year

  const reductionPct = s.priceReductionPct;
  const timeReductionPct = s.timeReductionPct;

  const missingClient = !state.client.company_name.trim();
  const [showClientDialog, setShowClientDialog] = useState(false);
  const [pendingAction, setPendingAction] = useState(null); // "save" | "export"
  const [clientDraft, setClientDraft] = useState({ company_name: "", contact_name: "", contact_email: "" });

  const openClientDialog = (action) => {
    setClientDraft({
      company_name: state.client.company_name || "",
      contact_name: state.client.contact_name || "",
      contact_email: state.client.contact_email || "",
    });
    setPendingAction(action);
    setShowClientDialog(true);
  };

  const persistPayload = useCallback(() => ({
    client: state.client,
    language: "pt-PT",
    trailer: state.trailer,
    customer: state.customer,
    primo: state.primo,
  }), [state.client, state.trailer, state.customer, state.primo]);

  // Save (or update) the current simulation to the backend. Returns saved id (or existing id).
  const autoSaveSimulation = useCallback(async () => {
    try {
      if (state.id) {
        await updateSimulation(state.id, persistPayload());
        return state.id;
      }
      const saved = await createSimulation(persistPayload());
      setState((s2) => ({ ...s2, id: saved.id }));
      return saved.id;
    } catch (err) {
      console.error("autoSave failed", err);
      return state.id || null;
    }
  }, [state.id, persistPayload, setState]);

  const handleSave = async () => {
    if (missingClient) { openClientDialog("save"); return; }
    setSaving(true);
    try {
      await autoSaveSimulation();
      toast.success(t("common.saved"));
    } catch (err) {
      console.error(err);
      toast.error(t("common.error"));
    } finally {
      setSaving(false);
    }
  };

  const handleExport = () => {
    if (missingClient) { openClientDialog("export"); return; }
    setShowLangDialog(true);
  };

  const submitClientDialog = async () => {
    const name = (clientDraft.company_name || "").trim();
    if (!name) { toast.error(t("common.clientRequired")); return; }
    // commit into shared state
    const patch = { company_name: name, contact_name: clientDraft.contact_name || "", contact_email: clientDraft.contact_email || "" };
    setState((s2) => ({ ...s2, client: { ...s2.client, ...patch } }));
    setShowClientDialog(false);
    // give React a tick to apply the patch before continuing
    await new Promise((r) => setTimeout(r, 50));
    if (pendingAction === "save") {
      setSaving(true);
      try {
        await autoSaveSimulation();
        toast.success(t("common.saved"));
      } catch (_) { toast.error(t("common.error")); }
      finally { setSaving(false); }
    } else if (pendingAction === "export") {
      setShowLangDialog(true);
    }
    setPendingAction(null);
  };

  const generatePdf = useCallback(async (selectedLang) => {
    // ---- Sovereign-language validation ----
    // Refuse to generate a PDF with an unsupported language OR when
    // any required translation key is missing. This prevents leaking
    // English words into a Spanish PDF (or any other combination).
    if (!SUPPORTED_PDF_LANGS.includes(selectedLang)) {
      // eslint-disable-next-line no-console
      console.error(
        `[generatePdf] Language "${selectedLang}" is not supported by the PDF i18n system.`
      );
      toast.error(t("common.error"));
      return;
    }
    const validation = validatePdfTranslations(selectedLang);
    if (!validation.ok) {
      // eslint-disable-next-line no-console
      console.error(
        `[generatePdf] Aborting: missing PDF translations for language "${selectedLang}":`,
        validation.missing
      );
      toast.error(
        `Missing PDF translations for ${selectedLang}: ${validation.missing.slice(0, 3).join(", ")}${validation.missing.length > 3 ? "…" : ""}`
      );
      return;
    }

    setShowLangDialog(false);
    setPdfLang(selectedLang);
    setExporting(true);

    // Wait for React to re-render the PDF component with the new language
    await new Promise((r) => setTimeout(r, 400));

    // Auto-save so the client data + simulation persist server-side.
    let savedId = state.id;
    try {
      savedId = await autoSaveSimulation();
    } catch (_) { /* non-critical */ }

    const clientName = state.client.company_name || "draft";
    const safeName =
      clientName.replace(/[^a-zA-Z0-9À-ÿ ]/g, "").trim().replace(/\s+/g, "_") ||
      "draft";
    const langSuffix = selectedLang !== "pt-PT" ? `-${selectedLang}` : "";
    const variantSuffix = pdfVariant === "technical" ? "-tech" : "";
    const filename = `primo-report-${safeName}${langSuffix}${variantSuffix}`;

    try {
      // Wait for web fonts to finish loading before capturing the HTML
      if (typeof document !== "undefined" && document.fonts && document.fonts.ready) {
        try { await document.fonts.ready; } catch (_) { /* ignore */ }
      }

      // Preload the brand logo image so its src is embedded (browser has it in cache).
      const logoImg = pdfRef.current?.querySelector('img[src*="primo-logo"]');
      if (logoImg && !logoImg.complete) {
        await new Promise((resolve) => {
          const done = () => resolve();
          logoImg.addEventListener("load", done, { once: true });
          logoImg.addEventListener("error", done, { once: true });
          setTimeout(done, 1500);
        });
      }

      // Extra tick to make sure layout is settled.
      await new Promise((r) => setTimeout(r, 150));

      const pdfDoc = pdfRef.current?.querySelector(".pdf-doc");
      if (!pdfDoc) throw new Error("PDF document element not found in the DOM.");

      // ---------- Build a self-contained HTML string ----------
      // 1) Snapshot the .pdf-doc element's outerHTML.
      // 2) Rewrite any relative image / link URLs to absolute so Chromium can
      //    fetch them when it opens the temp file:// URL on the backend.
      // 3) Collect every accessible CSS rule from document.styleSheets and
      //    inline them into a single <style> tag.
      const origin = window.location.origin;

      // Clone so we can safely mutate URLs without affecting the live DOM.
      const clone = pdfDoc.cloneNode(true);

      clone.querySelectorAll("img[src]").forEach((img) => {
        const src = img.getAttribute("src");
        if (!src) return;
        if (/^(https?:|data:|blob:)/i.test(src)) return;
        img.setAttribute(
          "src",
          src.startsWith("/") ? `${origin}${src}` : `${origin}/${src}`
        );
      });

      // Extract all reachable CSS rules from the current document
      let allCss = "";
      for (const sheet of Array.from(document.styleSheets)) {
        try {
          const rules = sheet.cssRules || [];
          for (const rule of Array.from(rules)) {
            allCss += rule.cssText + "\n";
          }
        } catch (_) {
          // Cross-origin stylesheets throw when accessing cssRules; ignore.
        }
      }

      // Backend chromium overlay: force .pdf-doc visible, set @page, keep
      // web-fonts loading in case the CSS imports them by URL.
      const forceCss = `
        @page { size: A4 portrait; margin: 12mm 14mm; }
        html, body {
          background: #ffffff !important;
          color: #0a0a0a;
          margin: 0;
          padding: 0;
          font-family: "Inter", "Helvetica Neue", Arial, sans-serif;
          font-size: 10pt;
          line-height: 1.35;
          -webkit-print-color-adjust: exact !important;
          print-color-adjust: exact !important;
        }
        .pdf-doc { display: block !important; }
        .no-print { display: none !important; }
      `;

      const fullHtml = `<!DOCTYPE html>
<html lang="${selectedLang}">
<head>
<meta charset="utf-8">
<title>${filename}</title>
<link rel="preconnect" href="https://fonts.googleapis.com">
<link rel="preconnect" href="https://fonts.gstatic.com" crossorigin>
<link href="https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700&family=Space+Grotesk:wght@500;600;700&family=JetBrains+Mono:wght@400;500&display=swap" rel="stylesheet">
<style>${allCss}</style>
<style>${forceCss}</style>
</head>
<body>${clone.outerHTML}</body>
</html>`;

      // ---------- Try the backend renderer first, fall back to client-side print ----------
      // When the backend has headless Chromium installed (default on the VPS
      // via `apt install chromium`), /api/pdf/render returns a downloadable
      // PDF blob directly. If Chromium is missing the endpoint returns 500,
      // in which case we render the SAME self-contained `fullHtml` inside a
      // hidden iframe and trigger the browser's own Print → Save-as-PDF
      // dialog — this works in every browser, needs no server, and preserves
      // fonts, colours and language exactly as designed.

      let serverSucceeded = false;
      try {
        const response = await apiClient.post(
          "/pdf/render",
          { html: fullHtml, filename },
          { responseType: "blob", timeout: 60000 }
        );

        // Guard: some proxies return a 200 with an error JSON body — make sure
        // it really is a PDF (starts with the "%PDF-" magic bytes) before we
        // hand it to the user.
        const blob = new Blob([response.data], { type: "application/pdf" });
        const headBuf = await blob.slice(0, 5).arrayBuffer();
        const head = new TextDecoder().decode(headBuf);
        if (!head.startsWith("%PDF-")) {
          throw new Error(`Server returned a non-PDF payload: "${head}"`);
        }

        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = `${filename}.pdf`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        // Delay revoke so slower browsers finish the download first.
        setTimeout(() => URL.revokeObjectURL(url), 1500);
        toast.success(t("common.exportPdf") + " ✓");
        serverSucceeded = true;
      } catch (serverErr) {
        // eslint-disable-next-line no-console
        console.warn(
          "[generatePdf] backend PDF renderer failed — falling back to client-side print. Details:",
          serverErr
        );

        await new Promise((resolve, reject) => {
          try {
            const iframe = document.createElement("iframe");
            iframe.setAttribute(
              "style",
              "position:fixed;left:-9999px;top:0;width:210mm;height:297mm;border:0;visibility:hidden;"
            );
            iframe.setAttribute("title", filename);
            iframe.setAttribute("aria-hidden", "true");
            // Sandboxing must NOT block same-origin / scripts because the
            // print CSS relies on inline <style> tags and web fonts loaded
            // from Google Fonts.
            document.body.appendChild(iframe);

            let printed = false;
            let cleanupTimer = null;
            const cleanup = () => {
              if (cleanupTimer) { clearTimeout(cleanupTimer); cleanupTimer = null; }
              try {
                if (iframe.parentNode) iframe.parentNode.removeChild(iframe);
              } catch (_) { /* ignore */ }
              resolve();
            };

            iframe.onload = async () => {
              try {
                // Give web-fonts + images inside the iframe a moment to load.
                const idoc = iframe.contentDocument;
                if (idoc && idoc.fonts && idoc.fonts.ready) {
                  try { await idoc.fonts.ready; } catch (_) { /* ignore */ }
                }
                await new Promise((r) => setTimeout(r, 300));

                if (iframe.contentWindow) {
                  iframe.contentWindow.focus();
                  // afterprint fires after the user closes the print dialog.
                  iframe.contentWindow.addEventListener("afterprint", () => {
                    if (!printed) {
                      printed = true;
                      cleanup();
                    }
                  });
                  iframe.contentWindow.print();
                  // Safety net: if afterprint never fires (some browsers on
                  // some platforms don't emit it inside iframes), tear down
                  // the iframe after 30 seconds.
                  cleanupTimer = setTimeout(() => {
                    if (!printed) {
                      printed = true;
                      cleanup();
                    }
                  }, 30000);
                } else {
                  cleanup();
                }
              } catch (innerErr) {
                // eslint-disable-next-line no-console
                console.error("[generatePdf] iframe print failed:", innerErr);
                cleanup();
                reject(innerErr);
              }
            };
            iframe.onerror = (ev) => {
              // eslint-disable-next-line no-console
              console.error("[generatePdf] iframe load error:", ev);
              cleanup();
              reject(new Error("iframe load error"));
            };

            // Write the self-contained fullHtml into the iframe.
            iframe.srcdoc = fullHtml;
          } catch (setupErr) {
            reject(setupErr);
          }
        });

        toast.success(t("common.exportPdf") + " ✓");
      }

      // Log the export for history (best-effort — non-critical, works even
      // if the client-side fallback was used).
      try {
        const settings = await getSettings();
        await createPdfExport({
          simulation_id: savedId || null,
          client_name: state.client.company_name || "",
          language: selectedLang,
          exported_by: (settings && settings.tech_name) || "",
        });
      } catch (_) { /* non-critical */ }

      // Reference `serverSucceeded` so lint doesn't complain if a future
      // refactor removes the branch that reads it; also useful for debug.
      void serverSucceeded;
    } catch (err) {
      console.error("PDF generation error:", err);
      toast.error(t("common.error"));
    } finally {
      setPdfLang(null);
      setExporting(false);
    }
  }, [state, t, autoSaveSimulation, pdfVariant]);

  const money2 = (v) => v.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  const money0 = (v) => v.toLocaleString(undefined, { maximumFractionDigits: 0 });

  return (
    <div className="wizard-phase phase-in max-w-7xl mx-auto px-4 sm:px-6">
      <PhaseHeader
        eyebrow={t("phases.p5")}
        title={t("reveal.title")}
        subtitle={t("reveal.subtitle")}
      />

      {missingClient && (
        <div className="mb-2 flex items-start gap-2 border border-[color:var(--brand)]/40 bg-[color:var(--brand-soft)] px-3 py-2 rounded no-print" data-testid="missing-client-warning">
          <AlertCircle className="w-3.5 h-3.5 text-[color:var(--brand)] mt-0.5 shrink-0" />
          <div className="flex-1 min-w-0">
            <p className="text-xs text-[color:var(--text)]">{t("common.clientRequired")}</p>
          </div>
          <button
            onClick={() => openClientDialog("save")}
            data-testid="fix-client-btn"
            className="text-[10px] font-mono uppercase tracking-widest text-[color:var(--brand)] hover:underline shrink-0"
          >
            {t("client.title")} →
          </button>
        </div>
      )}

      {/* Quick client dialog — captures the minimum client info WITHOUT jumping back
          to Phase 1. Triggered when user hits Save / Export PDF and client name
          is missing. */}
      <Dialog open={showClientDialog} onOpenChange={setShowClientDialog}>
        <DialogContent className="bg-[color:var(--bg)] border-[color:var(--border)] max-w-md" data-testid="quick-client-dialog">
          <DialogHeader>
            <DialogTitle className="text-[color:var(--text)] font-display">
              {t("client.title")}
            </DialogTitle>
            <DialogDescription className="text-[color:var(--text-mute)]">
              {t("common.clientRequired")}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 mt-2">
            <div>
              <label htmlFor="qc-company" className="text-[10px] font-mono uppercase tracking-widest text-[color:var(--text-mute)] mb-1 block">
                {t("client.company")} *
              </label>
              <input
                id="qc-company"
                data-testid="quick-client-company"
                autoFocus
                type="text"
                value={clientDraft.company_name}
                onChange={(e) => setClientDraft((d) => ({ ...d, company_name: e.target.value }))}
                onKeyDown={(e) => { if (e.key === "Enter") submitClientDialog(); }}
                placeholder="Ex: TransRefrigerados Lda."
                className="w-full h-10 px-3 rounded border border-[color:var(--border)] bg-[color:var(--surface)] text-[color:var(--text)] text-sm focus:outline-none focus:border-[color:var(--brand)]"
              />
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div>
                <label htmlFor="qc-contact" className="text-[10px] font-mono uppercase tracking-widest text-[color:var(--text-mute)] mb-1 block">
                  {t("client.contact")}
                </label>
                <input
                  id="qc-contact"
                  data-testid="quick-client-contact"
                  type="text"
                  value={clientDraft.contact_name}
                  onChange={(e) => setClientDraft((d) => ({ ...d, contact_name: e.target.value }))}
                  onKeyDown={(e) => { if (e.key === "Enter") submitClientDialog(); }}
                  className="w-full h-10 px-3 rounded border border-[color:var(--border)] bg-[color:var(--surface)] text-[color:var(--text)] text-sm focus:outline-none focus:border-[color:var(--brand)]"
                />
              </div>
              <div>
                <label htmlFor="qc-email" className="text-[10px] font-mono uppercase tracking-widest text-[color:var(--text-mute)] mb-1 block">
                  Email
                </label>
                <input
                  id="qc-email"
                  data-testid="quick-client-email"
                  type="email"
                  value={clientDraft.contact_email}
                  onChange={(e) => setClientDraft((d) => ({ ...d, contact_email: e.target.value }))}
                  onKeyDown={(e) => { if (e.key === "Enter") submitClientDialog(); }}
                  className="w-full h-10 px-3 rounded border border-[color:var(--border)] bg-[color:var(--surface)] text-[color:var(--text)] text-sm focus:outline-none focus:border-[color:var(--brand)]"
                />
              </div>
            </div>
            <div className="flex items-center justify-end gap-2 pt-2">
              <Button
                variant="ghost"
                onClick={() => { setShowClientDialog(false); setPendingAction(null); }}
                data-testid="quick-client-cancel"
                className="h-9 px-3 text-[color:var(--text)] hover:bg-[color:var(--surface-2)] hover:text-[color:var(--text)] text-xs"
              >
                {t("common.cancel")}
              </Button>
              <Button
                onClick={submitClientDialog}
                data-testid="quick-client-submit"
                className="h-9 px-4 bg-[color:var(--brand)] hover:bg-[color:var(--brand-dim)] text-black font-semibold text-xs"
              >
                {pendingAction === "export" ? t("common.exportPdf") : t("common.save")}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* PDF container — hidden on screen, made visible by @media print CSS
          when the user triggers window.print() for PDF export. The `pdf-inline`
          class prevents the print stylesheet from hiding this wrapper (only
          non-PDF direct children of .phase-in are hidden). We only mount the
          PDF component once `pdfLang` is set (after language validation) so
          neither variant renders with the wrong language. */}
      <div ref={pdfRef} className="pdf-inline">
        {pdfLang ? (
          pdfVariant === "technical" ? (
            <PdfTechnicalReport state={state} overrideLang={pdfLang} logoDataUrl={logoDataUrl} />
          ) : (
            <PdfVisualReport state={state} overrideLang={pdfLang} logoDataUrl={logoDataUrl} />
          )
        ) : null}
      </div>

      {/* Language + variant selection dialog for PDF export */}
      <Dialog open={showLangDialog} onOpenChange={setShowLangDialog}>
        <DialogContent className="bg-[color:var(--bg)] border-[color:var(--border)] max-w-sm">
          <DialogHeader>
            <DialogTitle className="text-[color:var(--text)] font-display flex items-center gap-2">
              <Globe className="w-5 h-5 text-[color:var(--brand)]" />
              {t("common.exportPdf")}
            </DialogTitle>
            <DialogDescription className="text-[color:var(--text-mute)]">
              Escolha o formato e o idioma do PDF
            </DialogDescription>
          </DialogHeader>

          {/* Variant picker: Visual (default) vs Technical */}
          <div
            role="tablist"
            aria-label="PDF variant"
            className="grid grid-cols-2 gap-2 mt-2 mb-1"
            data-testid="pdf-variant-picker"
          >
            {[
              { code: "visual", label: "Visual" },
              { code: "technical", label: "Técnico" },
            ].map((v) => {
              const active = pdfVariant === v.code;
              return (
                <button
                  key={v.code}
                  type="button"
                  role="tab"
                  aria-selected={active}
                  data-testid={`pdf-variant-${v.code}`}
                  onClick={() => setPdfVariant(v.code)}
                  className={[
                    "h-9 rounded font-mono text-xs uppercase tracking-widest border transition-colors",
                    active
                      ? "bg-[color:var(--navy)] border-[color:var(--navy)] text-white"
                      : "bg-[color:var(--surface-2)] border-[color:var(--border)] text-[color:var(--text)] hover:border-[color:var(--navy)]",
                  ].join(" ")}
                >
                  {v.label}
                </button>
              );
            })}
          </div>

          <div className="grid grid-cols-1 gap-2 mt-2">
            {LANGUAGES.map((lng) => (
              <button
                key={lng.code}
                onClick={() => generatePdf(lng.code)}
                data-testid={`pdf-lang-${lng.code}`}
                className={[
                  "flex items-center gap-3 px-4 py-3 rounded border transition-colors text-left",
                  lng.code === appLang
                    ? "bg-[color:var(--brand-soft)] border-[color:var(--brand)] text-[color:var(--text)]"
                    : "bg-[color:var(--surface)] border-[color:var(--border)] text-[color:var(--text)] hover:border-[color:var(--brand)]",
                ].join(" ")}
              >
                <span className="text-xs font-mono font-bold w-6 text-center text-[color:var(--brand)]">
                  {lng.flag}
                </span>
                <span className="font-display font-semibold text-sm">
                  {lng.label}
                </span>
                {lng.code === appLang && (
                  <span className="ml-auto text-[9px] font-mono uppercase tracking-widest text-[color:var(--brand)]">
                    actual
                  </span>
                )}
              </button>
            ))}
          </div>
        </DialogContent>
      </Dialog>

      {/* Export loading overlay */}
      {exporting && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center">
          <div className="bg-[color:var(--bg)] border border-[color:var(--border)] rounded-lg px-6 py-4 flex items-center gap-3 shadow-2xl">
            <Loader2 className="w-5 h-5 text-[color:var(--brand)] animate-spin" />
            <span className="text-sm font-display text-[color:var(--text)]">A gerar PDF…</span>
          </div>
        </div>
      )}

      <div className="wizard-body">
        <div className="wizard-body-scroll grid grid-cols-1 lg:grid-cols-3 gap-3 auto-rows-min">
          {/* Hero savings tile — spans 2 cols */}
          <section className="card-tech relative overflow-hidden noise lg:col-span-2 lg:row-span-2">
            <div className="relative z-10 p-4 lg:p-6 flex flex-col h-full justify-center text-center">
              <div className="inline-flex items-center gap-2 border border-[color:var(--brand)] text-[color:var(--brand)] text-[9px] font-mono uppercase tracking-[0.22em] px-2.5 py-1 mb-3 self-center">
                <Award className="w-3 h-3" />
                {t("reveal.congrats")}
              </div>

              <div className="text-[10px] font-mono uppercase tracking-widest text-[color:var(--text-mute)] mb-1">
                {t("reveal.pricePerSet")}
              </div>
              <AnimatedNumber
                value={priceSavings}
                decimals={0}
                prefix="€ "
                duration={1600}
                className="text-6xl lg:text-7xl xl:text-8xl font-black font-display block text-[color:var(--brand)] leading-none break-words"
                data-testid="reveal-price-savings"
              />
              <div className="mt-1 text-[10px] font-mono text-[color:var(--text-mute)] tabular-num">
                = € {money2(priceSavings)}
              </div>
              <div className="mt-2 text-xs text-[color:var(--text-dim)] break-words">
                {t("reveal.reductionOf")}{" "}
                <span className="text-[color:var(--text)] font-mono font-semibold">{reductionPct.toFixed(1)}%</span>{" "}
                <span className="text-[color:var(--text-mute)]">•</span>{" "}
                € {money2(priceCurrent)}{" "}
                <span className="text-[color:var(--text-mute)]">→</span>{" "}
                € {money2(pricePrimo)}
              </div>

              {/* Time savings row */}
              <div className="mt-4 pt-3 border-t border-[color:var(--border)] flex items-center justify-around gap-4 text-left">
                <div className="flex items-center gap-2">
                  <Clock className="w-4 h-4 text-[color:var(--brand)] shrink-0" />
                  <div>
                    <div className="text-[9px] font-mono uppercase tracking-widest text-[color:var(--text-mute)]">
                      {t("reveal.timePerSet")}
                    </div>
                    <AnimatedNumber
                      value={timeSavingsH}
                      decimals={2}
                      suffix=" h"
                      className="text-lg font-bold text-[color:var(--text)] font-display block leading-tight"
                      data-testid="reveal-time-savings"
                    />
                    <div className="text-[10px] text-[color:var(--text-dim)] font-mono tabular-num">
                      = {timeSavingsMin.toLocaleString(undefined, { maximumFractionDigits: 0 })} min · {t("reveal.timeReduction")}:{" "}
                      <span className="text-[color:var(--brand)] font-mono">{timeReductionPct.toFixed(1)}%</span>
                    </div>
                  </div>
                </div>
                <div className="text-right">
                  <div className="text-[9px] font-mono uppercase tracking-widest text-[color:var(--text-mute)]">
                    {periodLabel}
                  </div>
                  <div className="font-mono text-base text-[color:var(--text)]" data-testid="reveal-period-time-savings">
                    {periodTimeH.toLocaleString(undefined, { maximumFractionDigits: 2 })} h
                  </div>
                  <div className="text-[10px] text-[color:var(--text-dim)] font-mono tabular-num">
                    {annualTimeH.toLocaleString(undefined, { maximumFractionDigits: 2 })} h / year
                  </div>
                </div>
              </div>
            </div>
          </section>

          {/* Sub metric 1 — period savings */}
          <div className="card-tech p-3">
            <div className="flex items-center gap-1.5 text-[9px] font-mono uppercase tracking-widest text-[color:var(--text-mute)] mb-1">
              <CalendarClock className="w-3 h-3" />
              {periodLabel}
            </div>
            <AnimatedNumber
              value={periodSavings}
              decimals={0}
              prefix="€ "
              className="text-2xl font-bold text-[color:var(--text)] font-display block leading-tight"
              data-testid="reveal-period-savings"
            />
            <div className="mt-1 text-[10px] text-[color:var(--text-dim)] font-mono tabular-num">
              = € {money2(periodSavings)}
            </div>
            <div className="mt-1 text-[10px] text-[color:var(--text-dim)]">
              {quantity} {t("common.units")} · {periodLabel.toLowerCase()}
            </div>
          </div>

          {/* Sub metric 2 — annualised */}
          <div className="card-tech p-3 border-[color:var(--brand)]/50">
            <div className="flex items-center gap-1.5 text-[9px] font-mono uppercase tracking-widest text-[color:var(--brand)] mb-1">
              <Building2 className="w-3 h-3" />
              {t("reveal.fleetSavings")}
            </div>
            <AnimatedNumber
              value={annualSavings}
              decimals={0}
              prefix="€ "
              className="text-2xl font-bold text-[color:var(--brand)] font-display block leading-tight"
              data-testid="reveal-fleet-savings"
            />
            <div className="mt-1 text-[10px] text-[color:var(--text-dim)] font-mono tabular-num">
              = € {money2(annualSavings)}
            </div>
            <div className="mt-1 text-[10px] text-[color:var(--text-dim)]">
              {quantity} · {periodLabel.toLowerCase()}
              {annualMultiplier > 1 ? ` × ${annualMultiplier}` : ""}
            </div>
          </div>

          {/* Client + trailer summary */}
          <section className="card-tech p-3 lg:col-span-3">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3 items-center">
              <div className="min-w-0">
                <div className="text-[9px] font-mono uppercase tracking-widest text-[color:var(--text-mute)] mb-0.5">
                  {t("client.title")}
                </div>
                <div className="text-[color:var(--text)] font-display text-sm break-words" data-testid="reveal-client-name">
                  {state.client.company_name || "—"}
                </div>
                {state.client.contact_name && (
                  <div className="text-xs text-[color:var(--text-dim)] break-words">{state.client.contact_name}</div>
                )}
              </div>
              <div className="min-w-0">
                <div className="text-[9px] font-mono uppercase tracking-widest text-[color:var(--text-mute)] mb-0.5">
                  {t("trailer.title")}
                </div>
                <div className="font-mono text-xs text-[color:var(--text)]">
                  {state.trailer.height_mm} × {state.trailer.width_mm} mm · {doors} portas
                </div>
                <div className="text-[10px] text-[color:var(--text-dim)] font-mono">
                  {quantity} {t("common.units")} / {periodLabel.toLowerCase()}
                </div>
              </div>
              <div className="flex items-center gap-2 justify-end no-print">
                <Button
                  onClick={handleSave}
                  disabled={saving}
                  data-testid="save-simulation-btn"
                  variant="outline"
                  className="h-9 px-3 border-[color:var(--border-strong)] hover:border-[color:var(--brand)] bg-transparent text-[color:var(--text)] hover:bg-[color:var(--surface-2)] hover:text-[color:var(--text)] text-xs"
                >
                  <Save className="w-3.5 h-3.5 mr-1.5" />
                  {saving ? t("common.loading") : t("common.save")}
                </Button>
                <Button
                  onClick={handleExport}
                  data-testid="export-pdf-btn"
                  className="h-9 px-3 bg-[color:var(--brand)] hover:bg-[color:var(--brand-dim)] text-black font-semibold text-xs"
                >
                  <Download className="w-3.5 h-3.5 mr-1.5" />
                  {t("common.exportPdf")}
                </Button>
              </div>
            </div>
          </section>
        </div>
      </div>

      {/* ===== PRESENTATION MODE ===== */}
      <PhaseFooter
        nextLabel={t("common.restart")}
        onNext={() => { window.location.href = "/"; }}
        nextTestId="restart-btn"
      />
    </div>
  );
}
